import { useState, useEffect } from 'react';
import { directusAPI } from '../../../api';
import { isCurrentPipelineSession, toPeriodKey } from '@prosecaptain/sentiment-rules';

const toWarsawDay = (date) => toPeriodKey(date, { granularity: 'day' });

/**
 * Widget funnel (impressions -> opens -> first messages) for a workspace, summed
 * from the widget_events_daily rollup over the Warsaw days covering [startDate, endDate].
 * Never throws: returns null when the collection does not exist yet (Directus 403/404),
 * on any other error, or when there are no rows (widget card stays hidden).
 */
export const fetchWidgetFunnel = async (workspaceId, startDate, endDate, token) => {
  if (!workspaceId) return null;
  try {
    const response = await directusAPI.get('/items/widget_events_daily', {
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      params: {
        filter: JSON.stringify({
          _and: [
            { linked_workspace: { _eq: workspaceId } },
            { day: { _gte: toWarsawDay(startDate) } },
            { day: { _lte: toWarsawDay(endDate) } }
          ]
        }),
        fields: ['day', 'impressions', 'opens', 'first_messages', 'errors'],
        limit: -1
      }
    });
    const rows = response?.data?.data || [];
    if (!rows.length) return null;
    const sum = (field) => rows.reduce((total, row) => total + (Number(row?.[field]) || 0), 0);
    return {
      impressions: sum('impressions'),
      opens: sum('opens'),
      firstMessages: sum('first_messages'),
      errors: sum('errors')
    };
  } catch (error) {
    return null;
  }
};

/**
 * Lightweight hook for the dashboard: only the funnel, last `dateRange` days.
 */
export const useWidgetFunnel = (workspaceId, dateRange = 30) => {
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(Boolean(workspaceId));

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId) {
      setFunnel(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange + 1);
    fetchWidgetFunnel(workspaceId, startDate, endDate, localStorage.getItem('directus_token')).then((result) => {
      if (cancelled) return;
      setFunnel(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, dateRange]);

  return { funnel, loading };
};

/**
 * Hook to fetch workspace-wide statistics
 */
export const useWorkspaceStatistics = (workspaceId, dateRange = 30) => {
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [stats, setStats] = useState({
    // Chat statistics
    totalSessions: 0,
    totalMessages: 0,
    totalLeads: 0,
    sessionsByDay: [],
    messagesByDay: [],
    sentimentStats: {
      positive: 0,
      negative: 0,
      neutral: 0
    },
    resolutionStats: {
      resolved: 0,
      unresolved: 0,
      resolutionPercentage: 0
    },
    // Article statistics
    totalArticles: 0,
    // Widget funnel from widget_events_daily, null = no tracking data
    widgetFunnel: null,
    articlesByDay: [],
    articlesByStatus: {
      completed: 0,
      draft: 0,
      published: 0,
      failed: 0
    }
  });

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!workspaceId) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('directus_token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Generate date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dateRange + 1);

        // Fetch chat sessions, articles and the widget funnel in parallel
        const [sessionsResponse, articlesResponse, widgetFunnel] = await Promise.all([
          // Chat sessions
          directusAPI.get('/items/chat_sessions', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: JSON.stringify({
                _and: [
                  { linked_workspace: { _nnull: true } },  // 🔒 ZAWSZE wykluczaj sesje bez workspace
                  { linked_workspace: { _eq: workspaceId } },
                  { date_created: { _gte: startDate.toISOString() } },
                  { date_created: { _lte: endDate.toISOString() } }
                ]
              }),
              fields: ['id', 'date_created', 'overall_sentiment', 'resolution_status', 'intent_fulfilled', 'messages_count', 'pipeline_version'],
              limit: 1000,
              meta: 'filter_count'
            }
          }),
          // Articles (filter by user's workspace)
          directusAPI.get('/items/articles', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: JSON.stringify({
                _and: [
                  { 
                    user_created: { 
                      linked_workspace: { _eq: workspaceId } 
                    } 
                  },
                  { date_created: { _gte: startDate.toISOString() } },
                  { date_created: { _lte: endDate.toISOString() } }
                ]
              }),
              fields: ['id', 'date_created', 'status'],
              limit: 1000,
              meta: 'filter_count'
            }
          }),
          // Same date range as the sessions; never rejects (null when not available)
          fetchWidgetFunnel(workspaceId, startDate, endDate, token)
        ]);

        const sessions = sessionsResponse.data.data || [];
        const hasCurrentPipeline = sessions.some(isCurrentPipelineSession);
        const pipelineSessions = hasCurrentPipeline
          ? sessions.filter(isCurrentPipelineSession)
          : sessions;
        const articles = articlesResponse.data.data || [];

        console.log('📊 Dashboard Stats Debug:', {
          workspaceId,
          workspaceIdType: typeof workspaceId,
          totalSessions: sessions.length,
          totalArticles: articles.length,
          dateRange: `${startDate.toISOString()} - ${endDate.toISOString()}`,
          sessionsMetaCount: sessionsResponse.data.meta?.filter_count,
          articlesMetaCount: articlesResponse.data.meta?.filter_count
        });

        // Process chat statistics (filter for sessions with messages)
        const sessionsWithMessages = pipelineSessions.filter(s => (s.messages_count || 0) > 1);
        
        console.log('🔍 Sessions filtering:', {
          totalSessions: sessions.length,
          sessionsWithMessages: sessionsWithMessages.length,
          sampleSession: sessions[0]
        });
        
        // Check if workspace has any meaningful data
        const hasAnyData = sessionsWithMessages.length > 0 || articles.length > 0;
        
        console.log('✅ Has data check:', {
          sessionsWithMessages: sessionsWithMessages.length,
          articles: articles.length,
          hasData: hasAnyData
        });
        
        setHasData(hasAnyData);

        if (!hasAnyData) {
          setStats((prev) => ({ ...prev, widgetFunnel }));
          console.log('⚠️ No data found in date range, showing default view');
          setLoading(false);
          return;
        }
        
        console.log('📈 Loading statistics...');
        
        let totalMessages = 0;
        const sessionsByDayMap = new Map();
        const messagesByDayMap = new Map();
        
        const sentimentCounts = { positive: 0, negative: 0, neutral: 0 };
        const resolutionCounts = { resolved: 0, unresolved: 0, total: 0 };

        sessionsWithMessages.forEach(session => {
          const date = new Date(session.date_created).toISOString().split('T')[0];
          const msgCount = session.messages_count || 0;
          
          totalMessages += msgCount;
          
          // Sessions by day
          sessionsByDayMap.set(date, (sessionsByDayMap.get(date) || 0) + 1);
          messagesByDayMap.set(date, (messagesByDayMap.get(date) || 0) + msgCount);
          
          // Sentiment
          if (session.overall_sentiment === 'positive') sentimentCounts.positive++;
          else if (session.overall_sentiment === 'negative') sentimentCounts.negative++;
          else if (session.overall_sentiment === 'neutral') sentimentCounts.neutral++;
          
          // Resolution
          const resolution =
            session.intent_fulfilled === 'true'
              ? 'resolved'
              : (session.intent_fulfilled === 'partial'
                ? 'partially_resolved'
                : (session.intent_fulfilled === 'false' ? 'unresolved' : session.resolution_status));
          if (resolution === 'resolved' || resolution === 'partially_resolved' || resolution === 'closed' || resolution === 'satisfied') {
            resolutionCounts.resolved++;
          } else if (resolution === 'unresolved' || resolution === 'open' || resolution === 'pending') {
            resolutionCounts.unresolved++;
          }
          if (resolution) resolutionCounts.total++;
        });

        // Process article statistics
        const articlesByStatus = { completed: 0, success: 0, draft: 0, published: 0, failed: 0, ongoing: 0 };
        const articlesByDayMap = new Map();

        articles.forEach(article => {
          const date = new Date(article.date_created).toISOString().split('T')[0];
          articlesByDayMap.set(date, (articlesByDayMap.get(date) || 0) + 1);
          
          const status = article.status;
          if (articlesByStatus.hasOwnProperty(status)) {
            articlesByStatus[status]++;
          }
          if (status === 'success') {
            articlesByStatus.completed++;
          }
        });

        // Fetch leads count
        let totalLeads = 0;
        if (sessions.length > 0) {
          try {
            const leadsResponse = await directusAPI.get('/items/crm', {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                filter: JSON.stringify({
                  linked_session: { _in: sessions.map(s => s.id) }
                }),
                meta: 'filter_count'
              }
            });
            totalLeads = leadsResponse.data.meta?.filter_count || 0;
          } catch (error) {
            console.error('Error fetching leads:', error);
          }
        }

        // Convert maps to arrays for charts
        const sessionsByDay = Array.from(sessionsByDayMap.entries())
          .map(([date, count]) => ({
            date,
            displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            count
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        const messagesByDay = Array.from(messagesByDayMap.entries())
          .map(([date, count]) => ({
            date,
            displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            count
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        const articlesByDay = Array.from(articlesByDayMap.entries())
          .map(([date, count]) => ({
            date,
            displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            count
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setStats({
          totalSessions: sessionsWithMessages.length,
          totalMessages,
          totalLeads,
          sessionsByDay,
          messagesByDay,
          sentimentStats: sentimentCounts,
          resolutionStats: {
            resolved: resolutionCounts.resolved,
            unresolved: resolutionCounts.unresolved,
            total: resolutionCounts.total,
            resolutionPercentage: resolutionCounts.total > 0 
              ? ((resolutionCounts.resolved / resolutionCounts.total) * 100).toFixed(1) 
              : 0
          },
          totalArticles: articles.length,
          widgetFunnel,
          articlesByDay,
          articlesByStatus
        });

      } catch (error) {
        console.error('❌ Error fetching workspace statistics:', error);
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error config:', error.config);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [workspaceId, dateRange]);

  return {
    loading,
    hasData,
    stats
  };
};

